/**
 * Scope Editor Section
 *
 * Generic, configurable two-column PCF-attribute / Revit-parameter editor.
 * Drives the duplicated table logic formerly copied between
 * ProcessConditionsSection and ProjectPropertiesSection.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  TableBody,
  TableCell,
  useTheme,
} from '@mui/material';
import { BodyText } from '../../../../shared/ui/typography';
import { Delete as DeleteIcon } from '@mui/icons-material';
import SearchableDropdown, { DropdownOption } from '../../../../shared/ui/forms/SearchableDropdown';
import { filterTextParameterOptions } from '../../../../shared/lib/revit/parameterOptionTypeFilter';
import TableHeaderField from '../../../../shared/ui/table/TableHeaderField';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { mappingSectionActionTooltips } from '../../../../shared/config/mappingHelpCatalog';
import {
  CompactAddAction,
  MappingEditorHeader,
  MappingRow,
  ScopeEditorTableVariant,
  scopeEditorDensity,
  getScopeEditorActionButtonSx,
  getScopeEditorDropdownSx,
} from '../../../../shared/ui/mapping/ScopeEditorPrimitives';
import { borderRadius, getSemanticSurfaceTokens } from '../../../../theme/theme';
import type {
  ScopeEditorPersistenceState,
  ScopeEditorPersistenceStateUpdater,
} from '../../lib/scopeEditorPersistence';
import { createScopeEditorPersistenceState } from '../../lib/scopeEditorPersistence';

export interface ScopeEditorAttributeDescriptor {
  id: string;
  defaultLabel: string;
  value: { value: string };
  onChange: (value: string) => void;
}

export interface ScopeEditorSectionProps {
  enabled: boolean;
  ariaLabel: string;
  disabledMessage: string;
  attributes: ScopeEditorAttributeDescriptor[];
  pcfAttributeOptions: DropdownOption[];
  revitParameterOptions: DropdownOption[];
  editorState?: ScopeEditorPersistenceState;
  onEditorStateChange?: (nextState: ScopeEditorPersistenceStateUpdater) => void;
}

const COLUMN_WIDTHS = {
  pcfAttribute: '42%',
  revitParam: '52%',
  actions: 36,
};

const ScopeEditorSection: React.FC<ScopeEditorSectionProps> = ({
  enabled,
  ariaLabel,
  disabledMessage,
  attributes,
  pcfAttributeOptions,
  revitParameterOptions,
  editorState,
  onEditorStateChange,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);

  // Scope attribute values are written as text; only text-typed Revit parameters
  // are offered (unknown-typed options stay visible — parameterOptionTypeFilter).
  const textRevitParameterOptions = useMemo(
    () => filterTextParameterOptions(revitParameterOptions),
    [revitParameterOptions]
  );

  const defaultPcfAttributeNames = useMemo(
    () =>
      attributes.reduce<Record<string, string>>((acc, attribute) => {
        acc[attribute.id] = attribute.defaultLabel;
        return acc;
      }, {}),
    [attributes]
  );

  const [localEditorState, setLocalEditorState] = useState<ScopeEditorPersistenceState>(() =>
    createScopeEditorPersistenceState(defaultPcfAttributeNames)
  );

  const [filters, setFilters] = useState({ pcfAttribute: '', revitParam: '' });
  const resolvedEditorState = editorState ?? localEditorState;
  const deletedRows = useMemo(
    () => new Set(resolvedEditorState.deletedRowIds),
    [resolvedEditorState.deletedRowIds]
  );
  const { customRows, pcfAttributeNames } = resolvedEditorState;

  const updateEditorState = (
    updater: (currentState: ScopeEditorPersistenceState) => ScopeEditorPersistenceState
  ) => {
    if (onEditorStateChange) {
      onEditorStateChange(updater);
      return;
    }
    setLocalEditorState((prevState) => updater(prevState));
  };

  const handleFilterChange = (field: 'pcfAttribute' | 'revitParam', value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handlePcfAttributeChange = (id: string, value: string) => {
    updateEditorState((currentState) => ({
      ...currentState,
      pcfAttributeNames: {
        ...currentState.pcfAttributeNames,
        [id]: value,
      },
    }));
  };

  const handleDeleteRow = (id: string) => {
    if (id.startsWith('custom-')) {
      updateEditorState((currentState) => ({
        ...currentState,
        customRows: currentState.customRows.filter((row) => row.id !== id),
      }));
      return;
    }

    updateEditorState((currentState) => ({
      ...currentState,
      deletedRowIds: Array.from(new Set([...currentState.deletedRowIds, id])),
    }));
  };

  const handleAddRow = () => {
    updateEditorState((currentState) => ({
      ...currentState,
      customRows: [
        ...currentState.customRows,
        {
          id: `custom-${Date.now()}`,
          label: '',
          value: '',
        },
      ],
    }));
  };

  const handleCustomRowChange = (id: string, field: 'label' | 'value', nextValue: string) => {
    updateEditorState((currentState) => ({
      ...currentState,
      customRows: currentState.customRows.map((row) =>
        row.id === id ? { ...row, [field]: nextValue } : row
      ),
    }));
  };

  const visibleAttributes = useMemo(
    () =>
      attributes
        .filter((attribute) => !deletedRows.has(attribute.id))
        .filter((attribute) => {
          const label = pcfAttributeNames[attribute.id] ?? attribute.defaultLabel;
          const matchesPcf = label.toLowerCase().includes(filters.pcfAttribute.toLowerCase());
          const matchesRevit = attribute.value.value.toLowerCase().includes(filters.revitParam.toLowerCase());
          return matchesPcf && matchesRevit;
        }),
    [attributes, deletedRows, filters, pcfAttributeNames]
  );

  const filteredCustomRows = useMemo(
    () =>
      customRows.filter((row) => {
        const matchesPcf = row.label.toLowerCase().includes(filters.pcfAttribute.toLowerCase());
        const matchesRevit = row.value.toLowerCase().includes(filters.revitParam.toLowerCase());
        return matchesPcf && matchesRevit;
      }),
    [customRows, filters]
  );

  if (!enabled) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          border: `1px dashed ${colors.border.strong}`,
          borderRadius: `${borderRadius.md}px`,
          backgroundColor: colors.background.card,
        }}
      >
        <BodyText secondary color="text.secondary">
          {disabledMessage}
        </BodyText>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <ScopeEditorTableVariant ariaLabel={ariaLabel} minWidth={500}>
        <MappingEditorHeader
          columns={[
            {
              key: 'pcfAttribute',
              width: COLUMN_WIDTHS.pcfAttribute,
              content: (
                <TableHeaderField
        label="PCF attribute target"
                  value={filters.pcfAttribute}
                  onChange={(value) => handleFilterChange('pcfAttribute', value)}
        ariaLabel="Filter PCF attribute targets"
                  inputHeight={scopeEditorDensity.inputHeight}
                />
              ),
            },
            {
              key: 'revitParam',
              width: COLUMN_WIDTHS.revitParam,
              content: (
                <TableHeaderField
        label="Revit source parameter"
                  value={filters.revitParam}
                  onChange={(value) => handleFilterChange('revitParam', value)}
        ariaLabel="Filter Revit source parameters"
                  inputHeight={scopeEditorDensity.inputHeight}
                />
              ),
            },
            {
              key: 'actions',
              width: COLUMN_WIDTHS.actions,
              minWidth: COLUMN_WIDTHS.actions,
              align: 'center',
              content: (
                  <BodyText component="span">Mapping actions</BodyText>
              ),
            },
          ]}
        />
        <TableBody>
          {visibleAttributes.map((attribute) => {
            const label = pcfAttributeNames[attribute.id] ?? attribute.defaultLabel;
            return (
              <MappingRow key={attribute.id}>
                <TableCell sx={{ width: COLUMN_WIDTHS.pcfAttribute }}>
                  <Box sx={{ minWidth: 0 }}>
                    <SearchableDropdown
                      value={label}
                      onChange={(value) => handlePcfAttributeChange(attribute.id, value)}
                      options={pcfAttributeOptions}
                      placeholder="Select or enter PCF attribute…"
                      freeSolo
                      ariaLabel={`PCF attribute target for ${attribute.id}`}
                      sx={getScopeEditorDropdownSx(theme)}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.revitParam }}>
                  <SearchableDropdown
                    value={attribute.value.value}
                    onChange={attribute.onChange}
                    options={textRevitParameterOptions}
                    placeholder="Select or enter Revit source parameter…"
                    freeSolo
                    ariaLabel={`Revit source parameter for ${label}`}
                    sx={getScopeEditorDropdownSx(theme)}
                  />
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.actions, textAlign: 'center' }}>
                  <ActionHelpTooltip help={mappingSectionActionTooltips.deleteAttribute}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRow(attribute.id)}
                      sx={getScopeEditorActionButtonSx(theme, 'danger')}
                      aria-label={`Delete ${label}`}
                    >
                      <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                    </IconButton>
                  </ActionHelpTooltip>
                </TableCell>
              </MappingRow>
            );
          })}

          {filteredCustomRows.map((row) => (
            <MappingRow key={row.id} accentColor={theme.palette.success.main}>
              <TableCell sx={{ width: COLUMN_WIDTHS.pcfAttribute }}>
                <Box sx={{ minWidth: 0 }}>
                  <SearchableDropdown
                    value={row.label}
                    onChange={(value) => handleCustomRowChange(row.id, 'label', value)}
                    options={pcfAttributeOptions}
                      placeholder="Select or enter PCF attribute…"
                    freeSolo
                      ariaLabel={`PCF attribute target for custom mapping ${row.id}`}
                    sx={getScopeEditorDropdownSx(theme)}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.revitParam }}>
                <SearchableDropdown
                  value={row.value}
                  onChange={(value) => handleCustomRowChange(row.id, 'value', value)}
                  options={textRevitParameterOptions}
                    placeholder="Select or enter Revit source parameter…"
                  freeSolo
                    ariaLabel={`Revit source parameter for ${row.label || 'custom PCF attribute'}`}
                  sx={getScopeEditorDropdownSx(theme)}
                />
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.actions, textAlign: 'center' }}>
                <ActionHelpTooltip help={mappingSectionActionTooltips.deleteAttribute}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRow(row.id)}
                    sx={getScopeEditorActionButtonSx(theme, 'danger')}
                    aria-label={`Delete ${row.label || 'attribute'}`}
                  >
                    <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                  </IconButton>
                </ActionHelpTooltip>
              </TableCell>
            </MappingRow>
          ))}

          <MappingRow>
            <TableCell colSpan={3} sx={{ py: 0.5 }}>
              <ActionHelpTooltip help={mappingSectionActionTooltips.addAttribute}>
      <CompactAddAction onClick={handleAddRow}>Add PCF mapping</CompactAddAction>
              </ActionHelpTooltip>
            </TableCell>
          </MappingRow>
        </TableBody>
      </ScopeEditorTableVariant>
    </Box>
  );
};

export default ScopeEditorSection;
